/**
 * Category-specific analysis prompt templates for improved accuracy
 * Maintains consistent output structure while tailoring analysis focus
 */

export enum ProductCategory {
  AUTO_DETECT = 'auto-detect',
  CLOTHING = 'clothing',
  SHOES = 'shoes',
  ELECTRONICS = 'electronics'
}

export interface CategoryOption {
  value: ProductCategory;
  label: string;
  icon: string;
  description: string;
}

export const CATEGORY_OPTIONS: CategoryOption[] = [
  {
    value: ProductCategory.AUTO_DETECT,
    label: 'Auto Detect',
    icon: '🔍',
    description: 'Let AI determine the product type automatically'
  },
  {
    value: ProductCategory.CLOTHING,
    label: 'Clothing & Apparel',
    icon: '👕',
    description: 'T-shirts, jeans, dresses, jackets, etc.'
  },
  {
    value: ProductCategory.SHOES,
    label: 'Shoes & Footwear',
    icon: '👟',
    description: 'Sneakers, boots, dress shoes, sandals, etc.'
  },
  {
    value: ProductCategory.ELECTRONICS,
    label: 'Electronics',
    icon: '📱',
    description: 'Phones, laptops, headphones, gaming devices, etc.'
  }
];

/**
 * Core instruction template that ensures consistent output structure
 * This is shared across all categories to maintain format consistency
 */
const CORE_OUTPUT_INSTRUCTIONS = `
Your goal is to provide information for a resale value estimation app.
Please respond strictly with a JSON object. Do not include any text outside of this JSON object, and do not use markdown fences (like \`\`\`json) around the JSON.

The JSON object must have the following keys:
1. "itemName": A concise and descriptive name for the item (string).
2. "itemDescription": A detailed description of the item, suitable for display to a user. Include characteristics like type, material, potential brand, condition (as observable from the image), and any notable features. This should be formatted as a Markdown string.
3. "estimatedValue": An estimated resale value range for the item (e.g., "$50 - $75", "Around $100"). Base this on the item's visual characteristics and common knowledge of similar items. (string).
4. "valuationRationale": An explanation of how the estimatedValue was derived. Mention factors considered, such as perceived condition, rarity, brand (if identifiable), and general market perception for such items. This should be formatted as a Markdown string.

Focus on visual analysis. Do not ask for more images or information. Provide the best possible analysis based on the single image provided.
`;

/**
 * Category-specific analysis focus instructions
 */
const CATEGORY_SPECIFIC_INSTRUCTIONS = {
  [ProductCategory.AUTO_DETECT]: `
Analyze the item in the provided image using general product identification techniques.
Examine all visible features to determine the item type and provide comprehensive analysis.
Pay attention to:
- Overall shape and form factor
- Material composition and texture
- Brand markings or logos (if visible)
- Construction quality and details
- Condition indicators
- Any unique identifying features
`,

  [ProductCategory.CLOTHING]: `
Analyze this clothing item with specific focus on apparel characteristics.
Pay special attention to:
- **Brand identification**: Look for logos, labels, tags, or distinctive brand elements
- **Fabric and material**: Texture, weight, composition indicators
- **Construction details**: Stitching quality, seam placement, hardware (buttons, zippers)
- **Fit and cut**: Silhouette, tailoring style, design elements
- **Condition assessment**: Wear patterns, fading, fabric integrity
- **Style identifiers**: Collar types, pocket styles, unique design features
- **Size indicators**: Visible size tags or proportional sizing
- **Care labels**: Washing instructions or material composition tags

Look for subtle details that indicate quality level and authenticity.
`,

  [ProductCategory.SHOES]: `
Analyze this footwear item with specific focus on shoe characteristics.
Pay special attention to:
- **Sole analysis**: Tread patterns, wear indicators, brand markings on outsole
- **Upper construction**: Material type (leather, canvas, synthetic), stitching patterns
- **Brand identification**: Logos on tongue, heel, sides, or insole
- **Hardware details**: Lacing systems, eyelets, buckles, or closure mechanisms
- **Shape and last**: Toe box shape, arch support, overall silhouette
- **Condition assessment**: Sole wear, upper scuffing, structural integrity
- **Size indicators**: Visible size markings on tongue, insole, or outsole
- **Technology features**: Cushioning systems, support elements, performance features
- **Style category**: Athletic, dress, casual, specialized (hiking, running, etc.)

Examine both the exterior design and any visible interior or sole markings.
`,

  [ProductCategory.ELECTRONICS]: `
Analyze this electronic device with specific focus on technology characteristics.
Pay special attention to:
- **Brand and model identification**: Logos, model numbers, product markings
- **Physical design**: Form factor, button layout, port configurations
- **Screen characteristics**: Size, bezel design, display quality (if visible)
- **Build quality indicators**: Material finish, construction quality, premium features
- **Connectivity ports**: USB, headphone jacks, charging ports, expansion slots
- **Condition assessment**: Scratches, dents, screen condition, general wear
- **Accessories visible**: Charging cables, cases, stands, or included items
- **Generation indicators**: Design language that suggests product age/generation
- **Functional elements**: Power buttons, volume controls, special function keys
- **Serial numbers or identifiers**: Any visible product codes or manufacturing info

Look for specific technical details that help identify the exact model and generation.
`
};

/**
 * Generate complete prompt for a specific category
 */
export function generateCategoryPrompt(category: ProductCategory): string {
  const specificInstructions = CATEGORY_SPECIFIC_INSTRUCTIONS[category];
  
  return `${specificInstructions}
${CORE_OUTPUT_INSTRUCTIONS}

Example for "itemDescription": "A vintage-style ceramic teapot with a floral pattern. Appears to be in good condition with no visible chips or cracks. The lid is present. Unknown brand."
Example for "valuationRationale": "The teapot's vintage aesthetic and good apparent condition suggest a moderate resale value. Floral ceramic teapots are popular among collectors. Without brand identification or a view of maker's marks, the estimate is based on general market prices for similar unbranded items."`;
}

/**
 * Get category by value with fallback
 */
export function getCategoryOption(value: string): CategoryOption {
  return CATEGORY_OPTIONS.find(option => option.value === value) || CATEGORY_OPTIONS[0];
}
